import { useState } from 'react';
import { t, fmt } from '@/i18n';
import { Input } from '@/components/ui/input';
import { getEmailSuggestion, isValidEmailFormat } from '@/lib/emailSuggestion';

interface EmailFieldProps {
  id: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  label: string;
  required?: boolean;
  placeholder?: string;
  /** Server-side error(s) for this field, if any. */
  error?: string;
}

/**
 * Email input with on-blur typo suggestion ("Did you mean gmail.com?") and
 * format validation. Suggests, never auto-corrects — the user applies the fix
 * on click. Shared across the registration form and any other email entry.
 */
export default function EmailField({ id, name, value, onChange, label, required = false, placeholder, error }: EmailFieldProps) {
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [formatError, setFormatError] = useState<string | null>(null);

  function handleBlur() {
    const trimmed = value.trim();
    if (!trimmed) {
      setSuggestion(null);
      setFormatError(null);
      return;
    }
    setFormatError(isValidEmailFormat(trimmed) ? null : t('Please enter a valid email address.'));
    setSuggestion(getEmailSuggestion(trimmed));
  }

  function applySuggestion() {
    if (!suggestion) return;
    onChange(suggestion);
    setSuggestion(null);
    setFormatError(null);
  }

  function handleChange(next: string) {
    onChange(next);
    if (suggestion) setSuggestion(null);
    if (formatError) setFormatError(null);
  }

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <Input
        id={id}
        name={name}
        type="email"
        required={required}
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={handleBlur}
        placeholder={placeholder ?? 'you@example.com'}
        className="mt-1"
      />
      {suggestion && (
        <button
          type="button"
          onClick={applySuggestion}
          className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-amber-600 hover:text-amber-700 dark:text-amber-500 dark:hover:text-amber-400"
        >
          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" />
          </svg>
          {fmt('Did you mean %(email)s?', { email: suggestion })}
        </button>
      )}
      {formatError && !error && <p className="mt-1 text-xs text-amber-600 dark:text-amber-500">{formatError}</p>}
      {error && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}
